import React, { useState, useEffect, useRef } from "react";
import styles from "./InvoicePayment.module.scss";
import classNames from "classnames/bind";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import { PlusIcon } from "~/assets/icons";
import dayjs from "dayjs";
import LogPaymentModal from "~/pages/admin/jobs/job-detail/tabs/financial/component/main-content/tabs/log-payment-modal/LogPaymentModal";
import RequestPaymentModal from "~/pages/admin/jobs/job-detail/tabs/financial/component/main-content/tabs/request-payment-modal/RequestPaymentModal";

const cx = classNames.bind(styles);

export interface IInvoicePayment {
  paymentDate: string;
  reference: string;
  amount: string;
  method: string;
  status: string;
}

interface IInvoicePaymentProps {
  invoicePayment: IInvoicePayment[] | null;
  idJob: string;
  onPaymentSuccess?: () => void;
  invoiceId: string;
  onProcessPaymentNow?: () => void;
}

const InvoicePayment = (props: IInvoicePaymentProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [isLogPaymentModalOpen, setIsLogPaymentModalOpen] = useState(false);
  const [isRequestPaymentModalOpen, setIsRequestPaymentModalOpen] =
    useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const calculateDropdownPosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      const dropdownWidth = 200;
      const spacing = 4;
      const padding = 8;

      let left = rect.left;
      if (left + dropdownWidth > viewportWidth - padding) {
        left = Math.max(padding, viewportWidth - dropdownWidth - padding);
      }
      if (left < padding) {
        left = padding;
      }

      const spaceBelow = viewportHeight - rect.bottom - spacing - padding;
      const spaceAbove = rect.top - spacing - padding;

      let top: number;
      if (spaceBelow < 150 && spaceAbove > spaceBelow) {
        top = rect.top - 150 - spacing;
        if (top < padding) {
          top = padding;
        }
      } else {
        top = rect.bottom + spacing;
        if (top + 150 > viewportHeight - padding) {
          top = Math.max(padding, rect.top - 150 - spacing);
        }
      }

      return { top, left };
    }
    return null;
  };

  const handleToggleDropdown = () => {
    if (!isDropdownOpen) {
      requestAnimationFrame(() => {
        const position = calculateDropdownPosition();
        if (position) {
          setDropdownPosition(position);
          setIsDropdownOpen(true);
        }
      });
    } else {
      setIsDropdownOpen(false);
      setDropdownPosition(null);
    }
  };

  const handleRequestPayment = () => {
    setIsDropdownOpen(false);
    setDropdownPosition(null);
    setIsRequestPaymentModalOpen(true);
  };

  const handleProcessPaymentNow = () => {
    setIsDropdownOpen(false);
    setDropdownPosition(null);
    props?.onProcessPaymentNow?.();
  };

  const handleLogPayment = () => {
    setIsDropdownOpen(false);
    setDropdownPosition(null);
    setIsLogPaymentModalOpen(true);
  };

  const handleCloseLogPaymentModal = () => {
    setIsLogPaymentModalOpen(false);
  };

  const handleCloseRequestPaymentModal = () => {
    setIsRequestPaymentModalOpen(false);
  };


  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isDropdownOpen) {
        const target = event.target as Node;
        if (
          buttonRef.current &&
          !buttonRef.current.contains(target) &&
          dropdownRef.current &&
          !dropdownRef.current.contains(target)
        ) {
          setIsDropdownOpen(false);
          setDropdownPosition(null);
        }
      }
    };

    const handleScroll = () => {
      if (isDropdownOpen) {
        setIsDropdownOpen(false);
        setDropdownPosition(null);
      }
    };

    const handleResize = () => {
      if (isDropdownOpen) {
        requestAnimationFrame(() => {
          const position = calculateDropdownPosition();
          if (position) {
            setDropdownPosition(position);
          } else {
            setIsDropdownOpen(false);
            setDropdownPosition(null);
          }
        });
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, { passive: true, capture: true });
    document.addEventListener("scroll", handleScroll, { passive: true, capture: true });
    window.addEventListener("resize", handleResize);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, { capture: true } as EventListenerOptions);
      document.removeEventListener("scroll", handleScroll, { capture: true } as EventListenerOptions);
      window.removeEventListener("resize", handleResize);
    };
  }, [isDropdownOpen]);

  return (
    <div className={cx("invoice-payment-container")}>
      <div className={cx("header")}>
        <h3 className={cx("title")}>Invoice Payment</h3>

        <div ref={buttonRef} className={cx("add-payment-wrapper")}>
          <PrimaryButton
            title="Add Payment"
            icon={<PlusIcon className={cx("plus-icon")} />}
            onClick={handleToggleDropdown}
            className={cx("add-payment-button")}
          />
          {isDropdownOpen && dropdownPosition && (
            <div
              ref={dropdownRef}
              className={cx("payment-dropdown")}
              data-payment-dropdown
              style={{
                top: `${dropdownPosition.top}px`,
                left: `${dropdownPosition.left}px`,
              }}
            >
              <div
                className={cx("dropdown-item")}
                onClick={handleRequestPayment}
              >
                Request Payment
              </div>
              <div
                className={cx("dropdown-item")}
                onClick={handleProcessPaymentNow}
              >
                Process Payment Now
              </div>
              <div className={cx("dropdown-item")} onClick={handleLogPayment}>
                Log Payment
              </div>
            </div>
          )}
        </div>
      </div>
      <div className={cx("sections-container")}>
        <div className={cx("table-wrapper")}>
          <table className={cx("sections-table")}>
            <thead>
              <tr>
                <th className={cx("col-payment-date")}>Payment Date</th>
                <th className={cx("col-reference")}>Reference #</th>
                <th className={cx("col-amount")}>Amount</th>
                <th className={cx("col-method")}>Method</th>
                <th className={cx("col-status")}>Status</th>
              </tr>
            </thead>
            <tbody>
              {props?.invoicePayment?.map((payment, paymentIndex) => (
                <tr key={paymentIndex} className={cx("item-row")}>
                  <td className={cx("col-payment-date")}>
                    {dayjs(payment.paymentDate).format("MM/DD/YYYY")}
                  </td>
                  <td className={cx("col-reference")}>{payment.reference}</td>
                  <td className={cx("col-amount")}>${payment.amount}</td>
                  <td className={cx("col-method")}>{payment.method}</td>
                  <td className={cx("col-status")}>{payment.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <LogPaymentModal
        idJob={props?.idJob}
        open={isLogPaymentModalOpen}
        onClose={handleCloseLogPaymentModal}
        onSuccess={props?.onPaymentSuccess}
        invoiceId={props?.invoiceId}
      />
      <RequestPaymentModal
        jobId={props?.idJob}
        open={isRequestPaymentModalOpen}
        onClose={handleCloseRequestPaymentModal}
        onSuccess={props?.onPaymentSuccess}
      />
    </div>
  );
};

export default InvoicePayment;
